import {
  ActivateCreditMonitoringError,
  ActivateCreditMonitoringInput,
  ActivateCreditMonitoringSuccess,
  AgreementType,
  ApplicationProductTypeEnum,
  Typenames,
} from '@varo/graphql-schema-types';
import backgroundTextureForest from 'assets/images/vxl/component/productIntroScreen/background-texture-forest.png';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useActivateCreditMonitoring } from 'features/creditScore/hooks/useActivateCreditMonitoring';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageSourcePropType, StyleSheet, View } from 'react-native';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import useAgreements from 'shared/hooks/useAgreements/useAgreements';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const CheckCreditScore = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('creditScore', {
    keyPrefix: 'creditScorePage',
  });
  const [showError, setShowError] = useState(false);

  const {
    agreementsListDetails: agreementsData,
    errorModel: agreementsError,
    loading: errorLoading,
    refresh,
  } = useAgreements([ApplicationProductTypeEnum.CreditMonitoring]);
  const { submitCreditScoreApplication, submitApplicationResult } =
    useActivateCreditMonitoring();
  const { defaultMessage, graphqlErrorsTitle } = useDefaultErrorStrings();
  const agreementError = !!agreementsError?.messageBody;
  const {
    refreshTiles,
    fetching: refreshTilesFetching,
    error: refreshTilesError,
    data: refreshTilesData,
  } = useRefreshHomeTiles([HomeTileTypeIds.VXL_CARD_GROUP_TILE_FOR_YOU]);

  const submitApplicationError =
    submitApplicationResult.data as ActivateCreditMonitoringError;

  useViewDidFocusScreenAnalyticsEvent(
    showError
      ? {
          key: 'creditScoreError',
        }
      : {
          key: 'Credit Score Access',
        },
  );

  const handleSeeCreditScoreClick = async () => {
    const contentId = agreementsData?.find(
      (item) => item.type === AgreementType.BelieveCreditMonitoringAgreement,
    );
    const input: ActivateCreditMonitoringInput = {
      agreementIds: [contentId?.id as string],
    };
    const submitApplicationSuccess = (await submitCreditScoreApplication(
      input,
    )) as ActivateCreditMonitoringSuccess;

    if (submitApplicationSuccess?.success) {
      // Refresh the home tiles after ActivateCreditMonitoring
      refreshTiles();
      monitoringSuccessfulEvent();
    } else {
      setShowError(true);
    }
  };

  // Wait until home tile refresh is complete then navigate to about-credit-score
  useEffect(() => {
    if ((!refreshTilesFetching && refreshTilesData) || refreshTilesError) {
      router.replace('/credit/about-credit-score');
    }
  }, [refreshTilesData, refreshTilesError, refreshTilesFetching, router]);

  const handleTryAgain = () => {
    void handleSeeCreditScoreClick();
  };

  const handleComeBackLater = () => {
    router.push('/');
  };
  const monitoringSuccessfulEvent = usePressableAnalytics({
    analyticsId: 'monitoringSuccessfulTriggered',
  });

  return (
    <>
      <StatusBar style="light" />
      <View style={styles.container}>
        <ScreenTransitionOverlay
          isLoading={errorLoading}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={
            submitApplicationError?.__typename ===
              Typenames.ActivateCreditMonitoringError ||
            showError ||
            agreementsError?.messageBody ? (
              <ErrorScreen
                screenType={ErrorScreenType.Error}
                messageHeadline={
                  agreementsError?.messageHeadline ?? graphqlErrorsTitle
                }
                messageBody={
                  agreementsError?.messageBody
                    ? agreementsError?.messageBody
                    : defaultMessage
                }
                primaryButton={{
                  title: translatedText('tryAgainButton'),
                  onPress: agreementError ? refresh : handleTryAgain,
                  isLoading: submitApplicationResult?.fetching,
                  analytics: {
                    analyticsId: agreementError ? undefined : 'tryAgainClicked',
                  },
                }}
                secondaryButton={{
                  title: translatedText('comeBackLaterButton'),
                  onPress: handleComeBackLater,
                  analytics: {
                    analyticsId: 'comeBackLaterClicked',
                  },
                }}
                screenAnalyticsEventKey="credit-score"
              />
            ) : null
          }
        />
        <View style={styles.creditScoreContent}>
          <Stack.Screen
            options={{
              headerShown: true,
              headerBackVisible: true,
              headerTransparent: true,
              title: translatedText('headerTitle'),
              header: (props: NativeStackHeaderProps) => (
                <ExpoNavigationBar
                  {...props}
                  transparent
                  lightText
                  primaryIcon={
                    <Icon
                      iconKey="BACK"
                      iconSize="MD"
                      iconColor={theme.color.text.inverse}
                    />
                  }
                />
              ),
            }}
          />
          <ProductIntroScreen
            backgroundTexture={backgroundTextureForest as ImageSourcePropType}
            headlineText={translatedText('titleText')}
            headlineTextStyle="expressive"
            topPadding={90}
            primaryButton={{
              label: translatedText('button'),
              onPress: handleSeeCreditScoreClick,
              analyticsId: 'seeMyScoreClicked',
              isLoading:
                submitApplicationResult?.fetching || refreshTilesFetching,
            }}
            visual="GAUGE_B"
            eyebrowText={translatedText('freeCreditTrackingLabel')}
            bodyContent={
              <View style={styles.wrapper}>
                <View style={styles.bodyContent}>
                  <BodyBaseText numberOfLines={3} style={styles.textCenter}>
                    {translatedText('trackingYourScoreContent')}
                  </BodyBaseText>
                </View>
                <View style={styles.instruction} />
                <BodyXSText
                  numberOfLines={0}
                  style={styles.instructionTextSecondary}
                >
                  {translatedText('instruction')}
                </BodyXSText>
              </View>
            }
          />
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  bodyContent: {
    gap: theme.spacing.m,
    paddingBottom: theme.spacing.m,
  },
  container: {
    flex: 1,
  },
  creditScoreContent: { flex: 1 },
  instruction: {
    borderTopColor: theme.color.border.primarySolid,
    borderTopWidth: 1,
  },
  instructionTextSecondary: {
    color: theme.color.text.secondary,
    paddingVertical: theme.spacing.m,
  },
  textCenter: {
    textAlign: 'center',
  },
  wrapper: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
});

export default CheckCreditScore;
